import { useState, useEffect, useCallback } from "react";
import { Exercise } from "../../../types";
import exercicies from "../../../services/exercicies";
import { SwipeDirection, SwipeHistoryItem } from "../types";

interface UseClassificacaoParams {
  initialExercises?: Exercise[];
}

function shuffleExercises(list: Exercise[]): Exercise[] {
  const arr = [...list];
  for (let i = arr.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

export function useClassificacao({ initialExercises }: UseClassificacaoParams) {
  const [exercises, setExercises] = useState<Exercise[]>(
    initialExercises || [],
  );
  const [currentIndex, setCurrentIndex] = useState(0);
  const [yesCount, setYesCount] = useState(0);
  const [noCount, setNoCount] = useState(0);
  const [history, setHistory] = useState<SwipeHistoryItem[]>([]);
  const [isDone, setIsDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    if (initialExercises && initialExercises.length > 0) return;

    let isCancelled = false;

    const fetchExercises = async () => {
      try {
        setLoading(true);
        const perPage = 1500;
        let page = 1;
        let allExercises: Exercise[] = [];
        let hasNext = true;

        while (hasNext) {
          const pageResult = await exercicies.list_all_paginated("all_view", {
            page,
            per_page: perPage,
            only_mine: false,
          });

          allExercises = allExercises.concat(pageResult.data ?? []);
          hasNext = pageResult.hasNext && (pageResult.data ?? []).length > 0;
          page = pageResult.page + 1;
        }

        const yesFromBackend = allExercises.filter(
          (ex) => ex.classification === "yes",
        ).length;
        const noFromBackend = allExercises.filter(
          (ex) => ex.classification === "no",
        ).length;

        const unclassified = allExercises.filter((ex) => !ex.classification);
        const randomized = shuffleExercises(unclassified);
        if (!isCancelled) {
          setExercises(randomized);
          setYesCount(yesFromBackend);
          setNoCount(noFromBackend);
          if (unclassified.length === 0) {
            setIsDone(true);
          }
        }
      } catch (error) {
        console.error(
          "Erro ao carregar exercícios para classificação:",
          error,
        );
      } finally {
        if (!isCancelled) {
          setLoading(false);
        }
      }
    };

    fetchExercises();

    return () => {
      isCancelled = true;
    };
  }, [initialExercises, reloadToken]);

  const handleSwipe = useCallback(
    (direction: SwipeDirection) => {
      setHistory((h) => [...h, { index: currentIndex, direction }]);
      if (direction === "yes") setYesCount((c) => c + 1);
      else setNoCount((c) => c + 1);

      const currentExercise = exercises[currentIndex];
      if (currentExercise?.id) {
        void exercicies
          .updateFields(String(currentExercise.id), {
            classification: direction,
          })
          .then(({ ok, error }) => {
            if (!ok && error) {
              console.error(
                "Erro ao atualizar classificação do exercício:",
                error,
              );
            }
          })
          .catch((err) => {
            console.error(
              "Erro inesperado ao atualizar classificação do exercício:",
              err,
            );
          });
      }

      const next = currentIndex + 1;
      setCurrentIndex(next);
      if (next >= exercises.length) setIsDone(true);
    },
    [currentIndex, exercises],
  );

  const handleButtonSwipe = (direction: SwipeDirection) => {
    const stackArea = document.querySelector(".fs-stack");
    if (!stackArea) return;
    const topCard = stackArea.querySelector(
      ".fs-card.is-top",
    ) as HTMLElement & { flyOut?: (direction: SwipeDirection) => void };
    if (topCard && topCard.flyOut) {
      topCard.flyOut(direction);
    }
  };

  const handleUndo = () => {
    if (history.length === 0) return;
    const last = history[history.length - 1];
    setHistory((h) => h.slice(0, -1));
    setCurrentIndex(last.index);
    if (last.direction === "yes") setYesCount((c) => Math.max(0, c - 1));
    else setNoCount((c) => Math.max(0, c - 1));
    if (isDone) setIsDone(false);
  };

  const handleRestart = () => {
    setCurrentIndex(0);
    setYesCount(0);
    setNoCount(0);
    setHistory([]);
    setIsDone(false);
    setReloadToken((token) => token + 1);
  };

  const visibleExercises = exercises.slice(currentIndex, currentIndex + 3);

  return {
    exercises,
    visibleExercises,
    currentIndex,
    yesCount,
    noCount,
    isDone,
    loading,
    handleSwipe,
    handleButtonSwipe,
    handleUndo,
    handleRestart,
  };
}

